import { motion } from "framer-motion";
import { CIRCLE_OF_FIFTHS, circleLabel, circleAltLabel } from "@/theory/notes";

/**
 * Visual circle of fifths. Caller controls highlights and click handlers.
 * Inner ring shows relative minors (lowercase) so we can extend later.
 */

export interface CircleHighlight {
  /** 0-indexed in CIRCLE_OF_FIFTHS */
  index: number;
  color: string; // tailwind/raw color
  label?: string; // e.g. "I", "IV", "V"
  pulse?: boolean;
}

export interface CircleOfFifthsProps {
  size?: number;
  rootIndex?: number; // bigger circle around this segment
  highlights?: CircleHighlight[];
  onSelect?: (pc: string, index: number) => void;
  showRomanRing?: boolean;
}

export function CircleOfFifths({
  size = 360,
  rootIndex,
  highlights = [],
  onSelect,
  showRomanRing = true,
}: CircleOfFifthsProps) {
  const cx = size / 2;
  const cy = size / 2;
  const rOuter = size / 2 - 8;
  const rInner = rOuter * 0.72;
  const rMid = (rOuter + rInner) / 2;

  const SLICE = (Math.PI * 2) / 12;
  // 12 o'clock = C, then clockwise.
  const angleFor = (i: number) => -Math.PI / 2 + i * SLICE;

  const arcPath = (i: number, rA: number, rB: number) => {
    const a0 = angleFor(i) - SLICE / 2;
    const a1 = angleFor(i) + SLICE / 2;
    const x0 = cx + rA * Math.cos(a0);
    const y0 = cy + rA * Math.sin(a0);
    const x1 = cx + rA * Math.cos(a1);
    const y1 = cy + rA * Math.sin(a1);
    const x2 = cx + rB * Math.cos(a1);
    const y2 = cy + rB * Math.sin(a1);
    const x3 = cx + rB * Math.cos(a0);
    const y3 = cy + rB * Math.sin(a0);
    return `M ${x0} ${y0} A ${rA} ${rA} 0 0 1 ${x1} ${y1} L ${x2} ${y2} A ${rB} ${rB} 0 0 0 ${x3} ${y3} Z`;
  };

  const labelPos = (i: number, r: number) => {
    const a = angleFor(i);
    return { x: cx + r * Math.cos(a), y: cy + r * Math.sin(a) };
  };

  const highlightFor = (i: number) => highlights.find((h) => h.index === i);

  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        {/* outer ring background */}
        <circle cx={cx} cy={cy} r={rOuter} fill="#14171c" stroke="#262b34" strokeWidth={1} />
        <circle cx={cx} cy={cy} r={rInner} fill="#0b0d10" stroke="#262b34" strokeWidth={1} />

        {/* segments */}
        {CIRCLE_OF_FIFTHS.map((pc, i) => {
          const h = highlightFor(i);
          const isRoot = rootIndex === i;
          const fill = h?.color ?? "transparent";
          return (
            <g key={pc}>
              <motion.path
                d={arcPath(i, rOuter - 1, rInner + 1)}
                fill={fill}
                opacity={h ? 0.32 : 0}
                animate={{ opacity: h ? 0.32 : 0 }}
                transition={{ duration: 0.3 }}
              />
              <path
                d={arcPath(i, rOuter - 1, rInner + 1)}
                fill="transparent"
                stroke="#262b34"
                strokeWidth={1}
                className="cursor-pointer"
                onClick={() => onSelect?.(pc, i)}
              />
              {/* major label */}
              <text
                x={labelPos(i, rMid + 18).x}
                y={labelPos(i, rMid + 18).y}
                textAnchor="middle"
                dominantBaseline="middle"
                fill={h ? "#fff" : "#e9eaee"}
                style={{ pointerEvents: "none", fontWeight: 700, fontSize: 16 }}
              >
                {circleLabel(i)}
              </text>
              {/* enharmonic twin, only where both spellings are real keys */}
              {circleAltLabel(i) && (
                <text
                  x={labelPos(i, rMid + 36).x}
                  y={labelPos(i, rMid + 36).y}
                  textAnchor="middle"
                  dominantBaseline="middle"
                  fill="#9aa1ad"
                  style={{ pointerEvents: "none", fontSize: 9 }}
                >
                  {circleAltLabel(i)}
                </text>
              )}
              {/* root indicator */}
              {isRoot && (
                <circle
                  cx={labelPos(i, rMid + 18).x}
                  cy={labelPos(i, rMid + 18).y}
                  r={20}
                  fill="none"
                  stroke="#f59e0b"
                  strokeWidth={2}
                />
              )}
              {/* pulse ring for highlighted */}
              {h?.pulse && (
                <motion.circle
                  cx={labelPos(i, rMid + 18).x}
                  cy={labelPos(i, rMid + 18).y}
                  r={14}
                  fill="none"
                  stroke={h.color}
                  strokeWidth={2}
                  initial={{ scale: 0.8, opacity: 0 }}
                  animate={{ scale: [0.8, 1.4], opacity: [0.7, 0] }}
                  transition={{ duration: 1, repeat: Infinity }}
                />
              )}
              {/* roman numeral on inner ring */}
              {showRomanRing && h?.label && (
                <text
                  x={labelPos(i, rInner - 22).x}
                  y={labelPos(i, rInner - 22).y}
                  textAnchor="middle"
                  dominantBaseline="middle"
                  fill={h.color}
                  style={{ pointerEvents: "none", fontWeight: 700, fontSize: 13 }}
                >
                  {h.label}
                </text>
              )}
            </g>
          );
        })}

        {/* center logo dot */}
        <circle cx={cx} cy={cy} r={rInner * 0.55} fill="#0b0d10" stroke="#262b34" strokeWidth={1} />
        <text
          x={cx}
          y={cy - 6}
          textAnchor="middle"
          fill="#9aa1ad"
          style={{ fontSize: 11, letterSpacing: "0.18em", textTransform: "uppercase" }}
        >
          Circle of
        </text>
        <text
          x={cx}
          y={cy + 12}
          textAnchor="middle"
          fill="#e9eaee"
          style={{ fontSize: 18, fontWeight: 700, fontFamily: "Poppins" }}
        >
          Fifths
        </text>
      </svg>
    </div>
  );
}
